<template>
    <b-card no-body>
        <b-container class="p-0">
            <!-- LIGNE DE RAPELLE DU DISTRIBUTEUR SELECTIONNE -->
            <b-form-group
                :label="$t('Distributor') + ':'"
                label-cols="4"
                label-cols-lg="4"
                label-class="font-weight-bold text-dark pt-0 pb-2"
                label-align-sm="left"
                label-size="lg"
                class="bg-white rounded p-2">
                <div>
                    <b-img
                        :src="getLogo(Form.Name)"
                        :width="200" 
                        alt="Logo"/>
                </div>
            </b-form-group>
            <!-- BLOC INFORMATIONS GENERAL MANDAT-->
            <b-form-group
                :label="$t('CertificateInformation') + ':'"
                label-cols="4"
                label-cols-lg="12"
                label-class="font-weight-bold text-dark pt-0 pb-2"
                label-align-sm="left"
                label-size="lg"
                class="bg-white rounded p-2">
                <!-- SELECTION DU CONTRAT : uniquement pour les distributeurs exigeant un contrat (ex : ENEDIS, GRDF, SRD) -->
                <!-- ESL et GEREDIS ne nécessitent pas de contrat : le mandat peut être créé sans contrat -->
                <ValidationProvider v-if="Form.RequiresContract && contractOptions.length > 0" name="ContractType" rules="required">
                    <b-form-group
                        :label="$t('ContractType')"
                        label-for="Input_ContractType"
                        :label-cols-sm="4"
                        label-align-sm="left"
                        class="d-flex justify-content-center mb-1">
                        <!-- Bouton radio de sélection du type de mandat -->
                        <b-form-radio-group
                            buttons
                            :button-variant="'outline-' + Variant"
                            v-model="certificate.ContractRef"
                            :options="contractOptions"
                            value-field="ContractId"
                            text-field="Name"/>
                    </b-form-group>
                </ValidationProvider>
                <!-- TYPE DE CLIENT (PARTICULIER, PROFESSIONNEL)-->
                <ValidationProvider v-if="Object.keys(this.Form.CustomerTypesData).length > 1" name="CustomerType" rules="required">
                    <b-form-group
                        :label="$t('CustomerType')"
                        label-for="Input_CustomerType"
                        :label-cols-sm="4"
                        label-align-sm="left"
                        class="mb-1">
                        <b-form-radio-group
                            buttons
                            :button-variant="'outline-' + Variant"
                            v-model="customerType"
                            @change="InitCustomer()"
                            :options="GetCustomerOptions()"
                            value-field="Id"
                            text-field="Name"/>
                        <b-badge 
                            v-b-tooltip.noninteractive.hover 
                            :title="Form.CustomerTypesData[customerType]?.Description" 
                            circle 
                            :variant="Variant"
                            class="ml-2">
                            <b-icon font-scale="1.5" icon="info" />
                        </b-badge>
                    </b-form-group>
                </ValidationProvider>
                <!-- NOM DU MANDAT -->
                <ValidationProvider rules="required|min:3" :name="$t('CertificateName')" v-slot="{ errors, valid }">
                    <b-form-group
                        :label="$t('CertificateName')"
                        label-for="Input_CertificateName"
                        :label-cols-sm="4"
                        label-align-sm="left"
                        class="mb-1">
                        <b-form-input 
                            v-model="certificate.CertificateName"
                            v-mask="certificateNameMask"
                            @blur="trimCertificateName"
                            :state="errors.length > 0 ? false : (valid ? true : false)"/>
                        <span class="text-danger">{{ errors[0] }}</span>
                    </b-form-group>
                </ValidationProvider>
                <!-- DATE DE FIN DU MANDAT -->
                <ValidationProvider :rules="{ required: true, min_years: Form.MinYears, max_years: Form.MaxYears }" :name="$t('SelectEndDate')" v-slot="{ errors, valid }" >
                    <b-form-group 
                        :label="$t('SelectEndDate')"
                        :state="valid"
                        :label-cols-sm="4"
                        label-align-sm="left"
                        class="mb-1"
                        :invalid-feedback="errors[0]">
                        <DatePicker 
                            :Variant="Variant"
                            :Required="true"
                            :MinDate="minDate"
                            :MaxDate="maxDate"
                            :State.sync="valid"
                            v-model="certificate.EndDate"/>
                    </b-form-group>
                </ValidationProvider>
            </b-form-group>
            <!-- Composant de formulaire pour mandat en tant que Tier ou Direct-->
            <!-- TODO : VOIR COMMENT PASSER DES PARAMETRES SPECIFIQUES POUR LES DIFFERENTS FORMULAIRES -->
            <component 
                :is="Form.CustomerTypesData[customerType]?.Component" 
                :PhoneConfig="Form.PhoneConfig"
                :CustomerInfo.sync="certificate.CustomerInfo">
            </component>
        </b-container>
    </b-card>
</template>
<script lang="ts">

    /**
     * Composants Vue js
     */
    import { Component, InjectReactive, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
    import { Getter } from 'vuex-class';

    /**
     * Validation champs formulaire
     */
    import { ValidationProvider } from 'vee-validate';

    /**
     * Classes et énumérations
     */
    import { SMACertificate } from '@/types/Administration/SMA/SMACertificate';
    import { SMAContract, SMADistributorForm } from '@/types/Administration/SMA/SMADistributorForm';
    import { ParticularCustomerInformation, ProfessionalCustomerInformation } from '@/types/Administration/SMA/CustomerInfo';
    import ContractTypeEnum from '@/enums/Administration/SMA/ContractTypeEnum';
    import CustomerTypeEnum from '@/enums/Administration/SMA/CustomerTypeEnum';

    /**
     * Forumaires mandat
     */
    import ProfessionalForm from './ClientForm/ProfessionalForm.vue';
    import ParticularForm from './ClientForm/ParticularForm.vue';

    /**
     * Traductions
     */
    import locale from '../../locales/SMA.json';

    /**
     * Date
     */
    import DatePicker from '@/components/commons/DatePicker.vue';
    import moment from 'moment';
        
	@Component({
		components: {
            ProfessionalForm,
            ParticularForm,
            ValidationProvider,
            DatePicker
        },
        i18n: {messages:locale}
	})
    /**
     * Formulaire des données client
     */
	export default class ClientForm extends Vue {
    
        /**
         * Liste de mandats
         */
        @PropSync('Certificate', { default: () => {} })
        public certificate!: SMACertificate;

        /**
         * Formulaire distributeur
         */
        @Prop()
        public Form!: SMADistributorForm;

        /**
         * Mode du formulaire ("New" pour nouveau mandat, "Edit" pour modification)
         */
        @Prop()
        public Mode!: "New" | "Edit" | "Renew";

        /** 
         * Variant de couleur à appliquer sur le composant 
         */
        @InjectReactive()
        public Variant!: string;

        /**
         * Liste des contrats
         */
        @Getter('CertificateStore/GetContracts')
        protected contracts!: Array<SMAContract>;
            
        /**
         * Retourne l'ensemble des clés du dictionnaire des types de Client disponible pour le distributeur
         */
        private GetCustomerOptions(){
            return Object.keys(this.Form.CustomerTypesData).map(key => {
                return {
                    Id: key,
                    Name: this.Form.CustomerTypesData[key].Name
                };
            });
        }

        /**
         * Retourne l'url du logo du distributeur : en svg si disponible, sinon en webp
         * @param name Nom du distributeur
         */
        private getLogo(name: string) : string
        {
            try
            {
                return require(`@/assets/images/${name}.svg`);
            }
            catch
            {
                return require(`@/assets/images/${name}.webp`);
            }
        }

        /**
         * Contrats du client disponibles pour le distributeur du mandat
         * Lecture réactive du store : la liste des contrats peut être rechargée pendant la vie du formulaire
         */
        private get contractOptions(): Array<SMAContract>
        {
            return this.contracts.filter(p => p.Type == this.certificate.Distributor);
        }

        /**
         * Masque de saisie du nom du mandat : aucun espace en début de nom
         * et jamais deux espaces consécutifs. Un espace isolé reste saisissable en fin
         * pendant la frappe (nécessaire pour écrire plusieurs mots) : il est retiré
         * à la sortie du champ (trimCertificateName)
         * Fonction masque interprétée par la directive v-mask
         * @param value Valeur courante du champ
         */
        private certificateNameMask(value: string): Array<any>
        {
            // Valeur attendue : espaces de début retirés et espaces multiples réduits à un seul.
            // Le masque est construit sur cette valeur normalisée pour rester aligné
            // lorsqu'un caractère non conforme est écarté en cours de conformation (ex : collage).
            const chars = String(value ?? "").replace(/^\s+/, "").replace(/\s{2,}/g, " ").split("");
            const mask: Array<any> = [];

            for (let i = 0; i < chars.length; i++)
            {
                // Premier caractère et caractère suivant un espace : tout sauf un espace
                if (i == 0 || chars[i - 1] == " ")
                    mask.push(/\S/);
                else
                    mask.push(/[\s\S]/);
            }

            return mask;
        }

        /**
         * Retrait des espaces de début et de fin du nom du mandat à la sortie du champ
         */
        private trimCertificateName(): void
        {
            this.certificate.CertificateName = String(this.certificate.CertificateName ?? "").trim();
        }

        /**
         * Date minimum et maximum pour un mandat distributeur
         */
        private minDate: Date | null = null;
        private maxDate: Date | null = null;

        /**
         * Enumération des TYPES Mandat et Client
         */
        public sMACustomerTypeEnum = CustomerTypeEnum;
        public sMAContractTypeEnum = ContractTypeEnum;

        /**
         * Gestion type client
         */
        public customerType: CustomerTypeEnum = null;

        private classes: { [key: number] : new (...args: any[]) => any } = {
            1: ProfessionalCustomerInformation,
            2: ParticularCustomerInformation
        };

        private customerTypes: { [key: string] : CustomerTypeEnum } = {
            "ProfessionalCustomerInformation": CustomerTypeEnum.Professional,
            "ParticularCustomerInformation": CustomerTypeEnum.Particular
        };

        /**
         * Initialisation d'une nouvelle classe utilisateur
         */
        private InitCustomer() : void
        {
            const FormClass = this.classes[this.customerType];
            if (!FormClass) throw new Error("Type inconnu");
            
            this.certificate.CustomerInfo = new FormClass({});
        }

        /**
         * Sélection automatique du contrat lorsqu'un seul est disponible pour le distributeur
         * (la liste des contrats peut être chargée après l'ouverture du formulaire)
         * Avec plusieurs contrats, aucun choix n'est imposé à l'utilisateur
         * @param options Contrats disponibles pour le distributeur du mandat
         */
        @Watch('contractOptions', { immediate: true })
        private onContractOptionsChanged(options: Array<SMAContract>): void
        {
            if (this.Mode == "Edit")
                return;

            if (options.length == 1)
                this.certificate.ContractRef = options[0].ContractId;
        }

        /**
         * Chargement du composant
         */
        mounted() {
            this.minDate = moment().add(this.Form.MinYears, 'years').toDate();
            this.maxDate = moment().add(this.Form.MaxYears, 'years').toDate();

            // Nom du mandat : retrait des espaces de début et de fin de la valeur existante
            this.certificate.CertificateName = String(this.certificate.CertificateName ?? "").trim();

            if(this.Mode == "New"){
                this.customerType = this.Form.DefaultCustomer;
                this.InitCustomer();
            }

            if(this.Mode == "Edit"){
                this.customerType = this.customerTypes[this.certificate.CustomerInfo.Type];
            }

            if(this.Mode == "Renew"){
                this.customerType = this.Form.DefaultCustomer;
            }
        }
    }
    
</script>
<style scoped>
</style>